import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Code, 
  Building2, 
  Headphones, 
  ArrowRight,
  Monitor,
  Briefcase,
  Users,
  Globe
} from 'lucide-react';
import './ServicesPreview.css';

const ServicesPreview: React.FC = () => {
  const serviceCategories = [
    {
      icon: Code,
      title: 'IT Services',
      description: 'Comprehensive IT recruitment across all technology domains',
      services: [
        'Testing & QA',
        'Cloud Computing',
        'Data Analytics',
        'Mobile Development',
        'Cybersecurity',
        'AI & Machine Learning'
      ],
      color: '#2563eb'
    },
    {
      icon: Building2,
      title: 'Non-IT Services',
      description: 'Expert recruitment for traditional business sectors',
      services: [
        'Banking & Finance',
        'Healthcare',
        'Manufacturing',
        'Retail & FMCG',
        'Real Estate',
        'Media & Entertainment'
      ],
      color: '#059669'
    },
    {
      icon: Headphones,
      title: 'ITES Services',
      description: 'Complete business process outsourcing solutions',
      services: [
        'Customer Support',
        'Technical Support',
        'Back Office Operations',
        'Knowledge Process Outsourcing',
        'Chat Support',
        'Service Desk'
      ],
      color: '#dc2626'
    }
  ];

  const featuredServices = [
    {
      icon: Monitor,
      title: 'Software Development',
      description: 'Full-stack developers, mobile app developers, and software architects'
    },
    {
      icon: Briefcase,
      title: 'Business Consulting',
      description: 'Management consultants, business analysts, and strategy professionals'
    },
    {
      icon: Users,
      title: 'Customer Service',
      description: 'Customer support specialists, technical support agents, and service desk professionals'
    },
    {
      icon: Globe,
      title: 'Digital Marketing',
      description: 'SEO specialists, social media managers, and digital marketing experts'
    }
  ];

  return (
    <section className="services-preview section">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Our Services</h2>
          <p className="section-subtitle">
            What you do has a far greater impact than what you say! We deliver comprehensive 
            recruitment solutions across multiple industries.
          </p>
        </motion.div>

        <div className="services-categories">
          {serviceCategories.map((category, index) => (
            <motion.div
              key={category.title}
              className="service-category card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <div className="category-header">
                <div 
                  className="category-icon"
                  style={{ backgroundColor: category.color }}
                >
                  <category.icon size={32} />
                </div>
                <div className="category-info">
                  <h3>{category.title}</h3>
                  <p>{category.description}</p>
                </div>
              </div>
              
              <div className="category-services">
                <h4>Key Areas:</h4>
                <div className="services-grid">
                  {category.services.map((service, serviceIndex) => (
                    <motion.span
                      key={service}
                      className="service-tag"
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: serviceIndex * 0.05 }}
                      viewport={{ once: true }}
                    >
                      {service}
                    </motion.span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="featured-services"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <h3 className="featured-title">Featured Service Areas</h3>
          <div className="featured-grid">
            {featuredServices.map((service, index) => (
              <motion.div
                key={service.title}
                className="featured-service"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="featured-icon">
                  <service.icon size={24} />
                </div>
                <h4>{service.title}</h4>
                <p>{service.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesPreview;
